import { clsx } from "clsx";

import type { ShipmentDetail } from "~/features/reports/schemas/shipment-detail-schema.ts";
import { formatDateToNumericUTC } from "~/shared/components/trax-grid/date-cell/date-cell.tsx";
import { formatCurrency } from "~/shared/utils/string-fns.ts";

import { formatTimeFromDateTime } from "../../constants/shipment-detail-columns.tsx";
import styles from "./shipment-detail-card.module.css";

// Mirrors the scheduledPickupTime/scheduledDeliveryTime aliases the desktop
// grid computes in shipment-details-report.tsx so both views share one shape.
export type EnrichedShipmentDetail = ShipmentDetail & {
	scheduledPickupTime: string | null;
	scheduledDeliveryTime: string | null;
};

type ShipmentDetailCardProps = {
	readonly item: EnrichedShipmentDetail;
	readonly showRate: boolean;
};

type CardFieldProps = {
	readonly label: string;
	readonly value: string;
	readonly emphasize?: boolean;
	readonly align?: "left" | "right";
};

type InlineFieldProps = {
	readonly label: string;
	readonly value: string;
	readonly emphasize?: boolean;
};

const EMPTY_VALUE_PLACEHOLDER = "\u2014";

const CardField = ({
	label,
	value,
	emphasize,
	align = "left",
}: CardFieldProps) => (
	<div className={clsx(styles.field, align === "right" && styles.alignRight)}>
		<div className={styles.label}>{label}</div>
		<div className={clsx(styles.value, emphasize && styles.valueEmphasis)}>
			{value || EMPTY_VALUE_PLACEHOLDER}
		</div>
	</div>
);

// Label + value on one line, e.g. "Mode: LTL" — used for the fields the
// wireframe renders inline rather than stacked (label above, value below).
const InlineField = ({ label, value, emphasize }: InlineFieldProps) => (
	<div className={styles.inlineField}>
		<span className={styles.label}>{label}</span>{" "}
		<span className={clsx(styles.value, emphasize && styles.valueEmphasis)}>
			{value || EMPTY_VALUE_PLACEHOLDER}
		</span>
	</div>
);

export const ShipmentDetailCard = ({
	item,
	showRate,
}: ShipmentDetailCardProps) => {
	return (
		<div className={styles.card} data-testid={"shipment-detail-card"}>
			<div className={styles.headerRow}>
				<div className={styles.headerFields}>
					<InlineField
						emphasize
						label={"TQL PO #"}
						value={String(item.poNumber)}
					/>
					<InlineField label={"Mode:"} value={item.rateType ?? ""} />
				</div>
				{item.loadStatus ? (
					<span className={styles.statusBadge}>{item.loadStatus}</span>
				) : null}
			</div>
			<div className={styles.row}>
				<CardField
					label={"Scheduled Delivery Date"}
					value={formatDateToNumericUTC(item.deliveryApptDateTime)}
				/>
				<CardField
					align={"right"}
					label={"Scheduled Delivery Time"}
					value={formatTimeFromDateTime(item.scheduledDeliveryTime)}
				/>
			</div>
			<div className={styles.row}>
				<CardField label={"Origin City"} value={item.originCity ?? ""} />
				<CardField
					align={"right"}
					label={"Origin State"}
					value={item.originStateProvinceCode ?? ""}
				/>
			</div>
			<div className={styles.row}>
				<CardField
					label={"Destination City"}
					value={item.destinationCity ?? ""}
				/>
				<CardField
					align={"right"}
					label={"Destination State"}
					value={item.destinationStateProvinceCode ?? ""}
				/>
			</div>
			{showRate ? (
				<InlineField
					emphasize
					label={"Rate"}
					value={
						item.totalBillAmount == null
							? ""
							: formatCurrency(item.totalBillAmount)
					}
				/>
			) : null}
			<InlineField label={"Customer PO:"} value={item.customerPO ?? ""} />
		</div>
	);
};
